import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '../ErrorBoundary';
import { LanguageSwitcher } from '../LanguageSwitcher';

import { env } from '@/shared/config/env';
import { useAuth } from '@/shared/auth/useAuth';
import {
  useMerchantPortalNavVisibility,
  type MerchantPortalNavFeatureKey,
} from '@/shared/navigation/merchantPortalNavVisibility';
import { usePermissions } from '@/shared/permissions';
import type { PermissionCode } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { useThemeMode } from '@/shared/theme/useThemeMode';

const DRAWER_WIDTH = 268;

interface NavItem {
  label: string;
  icon: ReactNode;
  to: string;
  /** When set, the item is only shown if the current user holds this permission. */
  permission?: PermissionCode;
  /** Optional sidebar toggle key. When false, the item stays out of the menu. */
  featureKey?: MerchantPortalNavFeatureKey;
}

interface NavSection {
  /** Optional group heading shown above the items. */
  heading?: string;
  items: NavItem[];
}

// Grouped, permission-aware navigation. Operational sections (Products, Orders,
// Customers, Media) are top-level workspaces; each one's domain configuration
// lives behind a settings gear on its page. Cross-cutting, store-level
// configuration lives under the grouped Settings area.
const NAV_SECTIONS: NavSection[] = [
  {
    items: [{ label: 'Dashboard', icon: <SpaceDashboardRoundedIcon />, to: '/' }],
  },
  {
    heading: 'Каталог',
    items: [
      {
        label: 'Товары',
        icon: <Inventory2RoundedIcon />,
        to: '/catalog/products',
        permission: 'catalog.view',
      },
      {
        label: 'Категории',
        icon: <CategoryRoundedIcon />,
        to: '/catalog/categories',
        permission: 'catalog.view',
      },
      {
        label: 'Цены',
        icon: <SellRoundedIcon />,
        to: '/catalog/pricing',
        permission: 'catalog.pricing.view',
        featureKey: 'pricing',
      },
    ],
  },
  {
    heading: 'Store',
    items: [
      {
        label: 'Store',
        icon: <StoreRoundedIcon />,
        to: '/store',
        permission: 'settings.store.view',
        featureKey: 'store',
      },
      {
        label: 'Orders',
        icon: <ReceiptLongRoundedIcon />,
        to: '/orders',
        permission: 'settings.orders.view',
        featureKey: 'orders',
      },
      {
        label: 'Customers',
        icon: <GroupsRoundedIcon />,
        to: '/customers',
        permission: 'customers.view',
        featureKey: 'customers',
      },
      {
        label: 'Media',
        icon: <PermMediaRoundedIcon />,
        to: '/media',
        permission: 'settings.media.view',
        featureKey: 'media',
      },
      {
        label: 'Content',
        icon: <ArticleRoundedIcon />,
        to: '/content/pages',
        permission: 'content.view',
      },
    ],
  },
  {
    heading: 'Лояльность',
    items: [
      {
        label: 'Клиенты',
        icon: <CardGiftcardRoundedIcon />,
        to: '/loyalty/customers',
        permission: 'loyalty.view',
        featureKey: 'loyalty',
      },
      {
        label: 'Программа',
        icon: <WorkspacePremiumRoundedIcon />,
        to: '/loyalty/program',
        permission: 'loyalty.view',
        featureKey: 'loyalty',
      },
      {
        label: 'Пул карт',
        icon: <CreditCardRoundedIcon />,
        to: '/loyalty/pool',
        permission: 'loyalty.view',
        featureKey: 'loyalty',
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      {
        label: 'Team',
        icon: <ManageAccountsRoundedIcon />,
        to: '/team',
        permission: 'employees.manage',
        featureKey: 'team',
      },
      { label: 'Settings', icon: <SettingsRoundedIcon />, to: '/settings' },
    ],
  },
];

function isNavItemActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

function initialsFromName(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function calculateTenantSelectorWidth(
  tenantName: string | null,
  availableTenants: Array<{ name: string }>,
): number {
  const candidates = [tenantName ?? 'Select tenant', ...availableTenants.map((tenant) => tenant.name)];
  const widestTenant = candidates.reduce((widest, candidate) => {
    const characterCount = Array.from(candidate).length;
    const width = characterCount * 8.2 + candidate.split(' ').length * 2;
    return width > widest ? width : widest;
  }, 0);

  // Leading icon + text block gap + trailing chevron + horizontal padding.
  return Math.ceil(widestTenant + 88);
}

function BrandMark() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 38,
        height: 38,
        flexShrink: 0,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: (t) =>
          `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
        boxShadow: '0 6px 16px rgba(18, 124, 113, 0.32)',
      }}
    >
      <StorefrontRoundedIcon sx={{ fontSize: 21 }} />
    </Box>
  );
}

/** Tenant selector placeholder. Switches the active (mock) tenant. */
function TenantSelector() {
  const { tenantName, tenantId, availableTenants, setTenant } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectorWidth = useMemo(
    () => calculateTenantSelectorWidth(tenantName, availableTenants),
    [availableTenants, tenantName],
  );

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<StorefrontRoundedIcon />}
        endIcon={<ExpandMoreRoundedIcon />}
        sx={{
          width: selectorWidth,
          flexShrink: 0,
          justifyContent: 'flex-start',
          textAlign: 'left',
          px: 1.5,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { flexShrink: 0 },
          '& .MuiButton-startIcon': { mr: 1 },
          '& .MuiButton-endIcon': { ml: 0.75 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1 }}
          >
            Tenant
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {tenantName ?? 'Select tenant'}
          </Typography>
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          sx: { textAlign: 'left' },
        }}
        PaperProps={{
          sx: {
            minWidth: selectorWidth,
          },
        }}
      >
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
          Switch tenant
        </ListSubheader>
        {availableTenants.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === tenantId}
            onClick={() => {
              setTenant(option);
              setAnchorEl(null);
            }}
            sx={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'nowrap' }}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Light/dark color-mode toggle (OBZR design system). */
function ThemeModeToggle() {
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton color="inherit" aria-label="Toggle color mode" onClick={toggleMode}>
        {isDark ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
      </IconButton>
    </Tooltip>
  );
}

/** User profile placeholder with a logout action. */
function UserMenu() {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const initials = user ? initialsFromName(user.name) : '?';

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          aria-label="Open account menu"
        >
          <Avatar
            sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user?.name ?? 'Guest'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email ?? 'Not signed in'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem disabled>Profile</MenuItem>
        <MenuItem disabled>Preferences</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            logout();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

/** Drawer body: brand, navigation placeholders, and an environment footer. */
function DrawerContent() {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const navVisibility = useMerchantPortalNavVisibility();

  const sections = NAV_SECTIONS.map((section) => ({
    heading: section.heading,
    items: section.items.filter(
      (item) =>
        (!item.permission || hasPermission(item.permission)) &&
        (!item.featureKey || navVisibility[item.featureKey]),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1.25, px: 2.5 }}>
        <BrandMark />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap lineHeight={1.2}>
            {env.appName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Back office
          </Typography>
        </Box>
      </Toolbar>
      <Divider />

      <Box component="nav" aria-label="Primary" sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {sections.map((section, index) => (
          <List
            key={section.heading ?? `section-${index}`}
            subheader={
              section.heading ? (
                <ListSubheader component="div" sx={{ bgcolor: 'transparent' }}>
                  {section.heading}
                </ListSubheader>
              ) : undefined
            }
            sx={{ px: 1.5 }}
          >
            {section.items.map((item) => (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={RouterLink}
                  to={item.to}
                  selected={isNavItemActive(location.pathname, item.to)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ))}
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {env.appName} · {env.mode}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Application layout shell.
 *
 * Provides the top app bar (brand, tenant selector, notifications, user menu),
 * a responsive side navigation (permanent on desktop, temporary on mobile),
 * and the main content area where routed pages render via `<Outlet />`.
 */
export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <MenuRoundedIcon />
            </IconButton>
          ) : null}

          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <BrandMark />
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {env.appName}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TenantSelector />
          </Box>

          <LanguageSwitcher />

          <ThemeModeToggle />

          <Tooltip title="Notifications">
            <span>
              <IconButton color="inherit" aria-label="Notifications" disabled>
                <NotificationsNoneRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <UserMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="Main navigation"
      >
        {/* Mobile: temporary overlay drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          <DrawerContent />
        </Drawer>

        {/* Desktop: permanent docked drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          <DrawerContent />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
